import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Paperclip, Send, X, Plus, FileText, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { friendlyUploadError } from '../lib/errors';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const TZ = 'America/Vancouver';
  // Compare "today" in Vancouver, not the viewer's local day.
  const ymd = (dt) => dt.toLocaleDateString('en-CA', { timeZone: TZ });
  const same = ymd(d) === ymd(new Date());
  return same
    ? d.toLocaleTimeString('en-CA', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: true })
    : d.toLocaleDateString('en-CA', { timeZone: TZ, month: 'short', day: 'numeric' });
};

const roleColor = (role) => ({
  admin: 'bg-tge-navy text-white',
  manager: 'bg-blue-600 text-white',
  counsellor: 'bg-emerald-600 text-white',
  staff: 'bg-emerald-600 text-white',
  recruiter: 'bg-amber-600 text-white',
  student: 'bg-violet-600 text-white',
}[role] || 'bg-slate-500 text-white');

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '·';

/**
 * Reusable chat panel.
 * Props:
 *  - canInitiate: bool — show "New chat" + contact picker
 *  - currentUserId: string
 *  - onUnreadChange?: (totalCount: number) => void
 */
export default function ChatPanel({ canInitiate = false, currentUserId, onUnreadChange }) {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  // Search + role filter for the threads sidebar so admins can locate a
  // conversation quickly in a busy inbox.
  const [threadSearch, setThreadSearch] = useState('');
  const [threadRoleFilter, setThreadRoleFilter] = useState('all');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // ---- fetchers ----
  const fetchThreads = async () => {
    try {
      const res = await fetch(`${API}/api/chat/threads`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setThreads(data.threads || []);
      if (onUnreadChange) {
        const total = (data.threads || []).reduce((sum, t) => sum + (t.unread_count || 0), 0);
        onUnreadChange(total);
      }
    } catch (_) {}
  };

  const fetchMessages = async (threadId, opts = { silent: false }) => {
    if (!opts.silent) setLoadingMsgs(true);
    try {
      const res = await fetch(`${API}/api/chat/threads/${threadId}/messages`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.messages || []);
    } finally {
      if (!opts.silent) setLoadingMsgs(false);
    }
  };

  const markRead = async (threadId) => {
    await fetch(`${API}/api/chat/threads/${threadId}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    fetchThreads();
  };

  const fetchContacts = async () => {
    const res = await fetch(`${API}/api/chat/contacts`, { credentials: 'include' });
    if (!res.ok) return;
    const data = await res.json();
    setContacts(data.contacts || []);
  };

  // ---- effects ----
  useEffect(() => {
    fetchThreads();
    if (canInitiate) fetchContacts();
    const id = setInterval(() => {
      fetchThreads();
      if (activeThread) fetchMessages(activeThread.thread_id, { silent: true });
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.thread_id]);

  useEffect(() => {
    if (activeThread) {
      fetchMessages(activeThread.thread_id);
      markRead(activeThread.thread_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread?.thread_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ---- actions ----
  const startThread = async (userId) => {
    const res = await fetch(`${API}/api/chat/threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Could not start chat');
      return;
    }
    const t = await res.json();
    setShowContacts(false);
    setContactSearch('');
    await fetchThreads();
    setActiveThread(t);
  };

  const sendMessage = async () => {
    if (sending) return;
    const text = body.trim();
    if (!text && !attachment) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', text);
      if (attachment) fd.append('attachment', attachment);
      const res = await fetch(`${API}/api/chat/threads/${activeThread.thread_id}/messages`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to send');
        return;
      }
      setBody('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchMessages(activeThread.thread_id, { silent: true });
      fetchThreads();
    } catch (err) {
      alert(friendlyUploadError(err, 'Failed to send'));
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (msg) => {
    const res = await fetch(
      `${API}/api/chat/threads/${activeThread.thread_id}/attachments/${msg.message_id}`,
      { credentials: 'include' }
    );
    if (!res.ok) return alert('Could not download attachment');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = msg.attachment_name || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const deleteThread = async (threadId, peerName) => {
    if (!threadId) return;
    const ok = window.confirm(
      `Delete conversation with ${peerName || 'this person'}? All messages and attachments will be permanently removed.`
    );
    if (!ok) return;
    const res = await fetch(`${API}/api/chat/threads/${threadId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Could not delete conversation');
      return;
    }
    if (activeThread?.thread_id === threadId) {
      setActiveThread(null);
      setMessages([]);
    }
    await fetchThreads();
  };

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.subtitle || '').toLowerCase().includes(q)
    );
  }, [contacts, contactSearch]);

  // Available roles across the current thread list — used to only show
  // filter chips for roles that actually appear (staff won't need a
  // "student" chip if they've never chatted with one).
  const availableThreadRoles = useMemo(() => {
    const roles = new Set(threads.map((t) => t.peer?.role).filter(Boolean));
    return Array.from(roles);
  }, [threads]);

  const filteredThreads = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    return threads.filter((t) => {
      if (threadRoleFilter !== 'all' && t.peer?.role !== threadRoleFilter) return false;
      if (!q) return true;
      return (
        (t.peer?.name || '').toLowerCase().includes(q) ||
        (t.peer?.email || '').toLowerCase().includes(q) ||
        (t.last_message_preview || '').toLowerCase().includes(q)
      );
    });
  }, [threads, threadSearch, threadRoleFilter]);

  const onAttachClick = () => fileInputRef.current?.click();

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-220px)] min-h-[480px] border border-tge-navy/10 rounded-2xl bg-white overflow-hidden shadow-sm"
      data-testid="chat-panel"
    >
      {/* Threads sidebar */}
      <aside className="border-r border-tge-navy/10 flex flex-col">
        <div className="p-4 border-b border-tge-navy/5 flex items-center justify-between">
          <div>
            <div className="font-serif-display text-lg text-tge-navy leading-none">Conversations</div>
            <div className="text-xs text-tge-navy/50 mt-1">
              {filteredThreads.length}
              {filteredThreads.length !== threads.length ? ` of ${threads.length}` : ''} active
            </div>
          </div>
          {canInitiate && (
            <Button
              size="sm"
              onClick={() => setShowContacts(true)}
              className="bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full h-8 px-3 text-xs"
              data-testid="chat-new-btn"
            >
              <Plus size={14} className="mr-1" /> New
            </Button>
          )}
        </div>

        {threads.length > 0 && (
          <div className="px-3 pt-3 pb-2 border-b border-tge-navy/5 space-y-2" data-testid="chat-thread-filter-bar">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
              <Input
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder="Search conversations…"
                className="pl-8 h-8 text-xs"
                data-testid="chat-thread-search"
              />
            </div>
            {availableThreadRoles.length > 1 && (
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setThreadRoleFilter('all')}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                    threadRoleFilter === 'all'
                      ? 'bg-tge-navy text-white border-tge-navy'
                      : 'bg-white text-tge-navy/70 border-tge-navy/15 hover:border-tge-navy/40'
                  }`}
                  data-testid="chat-role-chip-all"
                >
                  All
                </button>
                {availableThreadRoles.map((r) => (
                  <button
                    key={r}
                    onClick={() => setThreadRoleFilter(r)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition capitalize ${
                      threadRoleFilter === r
                        ? 'bg-tge-navy text-white border-tge-navy'
                        : 'bg-white text-tge-navy/70 border-tge-navy/15 hover:border-tge-navy/40'
                    }`}
                    data-testid={`chat-role-chip-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {filteredThreads.length === 0 ? (
            <div className="text-center text-sm text-tge-navy/50 px-6 py-12">
              {threads.length === 0
                ? (canInitiate
                    ? 'No conversations yet. Click "New" to start one.'
                    : 'No messages yet. Your team will reach out here.')
                : 'No conversations match your search.'}
            </div>
          ) : (
            filteredThreads.map((t) => {
              const isActive = activeThread?.thread_id === t.thread_id;
              return (
                <div
                  key={t.thread_id}
                  className={`group relative w-full border-b border-tge-navy/5 transition flex items-center ${
                    isActive ? 'bg-tge-cream/60' : 'hover:bg-tge-cream/40'
                  }`}
                  data-testid={`chat-thread-row-${t.thread_id}`}
                >
                  <button
                    onClick={() => setActiveThread(t)}
                    className="flex-1 text-left px-4 py-3 flex gap-3 items-center min-w-0"
                    data-testid={`chat-thread-${t.thread_id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${roleColor(t.peer?.role)}`}>
                      {initials(t.peer?.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-medium text-tge-navy text-sm truncate">{t.peer?.name || 'Unknown'}</div>
                        <div className="text-[10px] text-tge-navy/40 shrink-0">{fmtTime(t.last_message_at)}</div>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <div className="text-xs text-tge-navy/60 truncate">
                          {t.last_message_preview || <span className="italic text-tge-navy/40">No messages yet</span>}
                        </div>
                        {t.unread_count > 0 && (
                          <span className="bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center font-semibold">
                            {t.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteThread(t.thread_id, t.peer?.name);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition text-tge-navy/40 hover:text-rose-600 hover:bg-rose-50 rounded-full w-8 h-8 flex items-center justify-center mr-2 shrink-0"
                    title="Delete conversation"
                    data-testid={`chat-thread-delete-${t.thread_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Active thread */}
      <section className="flex flex-col bg-tge-paper/40">
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center text-tge-navy/40 text-sm">
            Select a conversation to view messages
          </div>
        ) : (
          <>
            <header className="px-5 py-3 border-b border-tge-navy/10 bg-white flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm ${roleColor(activeThread.peer?.role)}`}>
                {initials(activeThread.peer?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-tge-navy text-sm leading-none truncate">
                  {activeThread.peer?.name}
                </div>
                <div className="text-[11px] text-tge-navy/50 mt-1 capitalize">
                  {activeThread.peer?.role} · {activeThread.peer?.email}
                </div>
              </div>
              <button
                onClick={() => deleteThread(activeThread.thread_id, activeThread.peer?.name)}
                className="text-tge-navy/40 hover:text-rose-600 hover:bg-rose-50 rounded-full w-9 h-9 flex items-center justify-center transition shrink-0"
                title="Delete conversation"
                data-testid="chat-delete-thread-btn"
              >
                <Trash2 size={16} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="chat-messages">
              {loadingMsgs && <div className="text-center text-xs text-tge-navy/40">Loading…</div>}
              {!loadingMsgs && messages.length === 0 && (
                <div className="text-center text-sm text-tge-navy/40 py-12">
                  No messages yet — say hello.
                </div>
              )}
              {messages.map((m) => {
                const mine = m.sender_id === currentUserId;
                return (
                  <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-2 shadow-sm ${
                        mine
                          ? 'bg-tge-navy text-white rounded-br-sm'
                          : 'bg-white border border-tge-navy/10 text-tge-navy rounded-bl-sm'
                      }`}
                    >
                      {!mine && (
                        <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5 font-medium">
                          {m.sender_name}
                        </div>
                      )}
                      {m.body && <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>}
                      {m.attachment_name && (
                        <button
                          onClick={() => downloadAttachment(m)}
                          className={`mt-2 flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg transition ${
                            mine ? 'bg-white/15 hover:bg-white/25' : 'bg-tge-cream hover:bg-tge-cream/70'
                          }`}
                          data-testid={`chat-attachment-${m.message_id}`}
                        >
                          <FileText size={14} />
                          <span className="truncate max-w-[180px]">{m.attachment_name}</span>
                        </button>
                      )}
                      <div className={`text-[10px] mt-1 ${mine ? 'text-white/60' : 'text-tge-navy/40'}`}>
                        {fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-tge-navy/10 bg-white px-3 py-3">
              {attachment && (
                <div className="flex items-center gap-2 text-xs px-3 py-1.5 bg-tge-cream/60 rounded-lg mb-2 w-fit">
                  <Paperclip size={12} /> <span className="truncate max-w-[200px]">{attachment.name}</span>
                  <button
                    onClick={() => {
                      setAttachment(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-tge-navy/50 hover:text-rose-500"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <button
                  onClick={onAttachClick}
                  className="w-9 h-9 rounded-full hover:bg-tge-cream/60 flex items-center justify-center text-tge-navy/60 hover:text-tge-navy transition shrink-0"
                  title="Attach file"
                  data-testid="chat-attach-btn"
                >
                  <Paperclip size={16} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 10 * 1024 * 1024) {
                      alert('File exceeds 10 MB limit');
                      e.target.value = '';
                      return;
                    }
                    setAttachment(f);
                  }}
                />
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  rows={1}
                  placeholder="Type a message..."
                  className="flex-1 resize-none border border-tge-navy/10 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:border-tge-navy/30 max-h-32"
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || (!body.trim() && !attachment)}
                  className="bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full h-9 w-9 p-0 shrink-0"
                  data-testid="chat-send-btn"
                >
                  <Send size={14} />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* New chat / contact picker modal */}
      {canInitiate && showContacts && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowContacts(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-tge-navy/10 flex items-center justify-between">
              <div className="font-serif-display text-tge-navy text-lg">Start a new chat</div>
              <button onClick={() => setShowContacts(false)} className="text-tge-navy/50 hover:text-tge-navy">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-tge-navy/5">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
                <Input
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Search by name, email or company..."
                  className="pl-9 text-sm"
                  data-testid="chat-contact-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredContacts.length === 0 ? (
                <div className="text-center text-sm text-tge-navy/50 py-10">No contacts found.</div>
              ) : (
                filteredContacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => startThread(c.id)}
                    className="w-full text-left px-4 py-3 hover:bg-tge-cream/40 border-b border-tge-navy/5 flex items-center gap-3"
                    data-testid={`chat-contact-${c.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm ${roleColor(c.role)}`}>
                      {initials(c.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-tge-navy truncate">{c.name}</div>
                      <div className="text-xs text-tge-navy/50 truncate">
                        {c.subtitle || c.role} · {c.email}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
